import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';

import css from './ImageLayoutContainer.module.css';

const ImageLayoutContainer = props => {
  const { intl, rootClassName, className, images } = props;
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  if (!images || images.length === 0) {
    return (
      <div className={css.noImage}>
        <div className={css.noImageContent}>
          <FormattedMessage id="ResponsiveImage.noImage" />
        </div>
      </div>
    );
  }

  const handleImageClick = index => {
    setSelectedImageIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedImageIndex(null);
  };

  const handlePreviousImage = () => {
    if (images.length > 1) {
      setSelectedImageIndex(selectedImageIndex > 0 ? selectedImageIndex - 1 : images.length - 1);
    }
  };

  const handleNextImage = () => {
    if (images.length > 1) {
      setSelectedImageIndex(selectedImageIndex < images.length - 1 ? selectedImageIndex + 1 : 0);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = event => {
      if (selectedImageIndex === null) return;

      switch (event.key) {
        case 'Escape':
          handleCloseModal();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          handlePreviousImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNextImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, images.length]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImageIndex]);

  // Touch gesture handling for mobile
  const handleTouchStart = e => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && images.length > 1) {
      handleNextImage();
    }
    if (isRightSwipe && images.length > 1) {
      handlePreviousImage();
    }
  };

  const renderImage = (image, index, className = '') => {
    const alt = intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: index + 1, count: images.length }
    );

    if (!image || !image.attributes || !image.attributes.variants) {
      return (
        <div
          key={index}
          className={classNames(css.imageWrapper, className)}
          onClick={() => handleImageClick(index)}
        >
          <div className={css.imagePlaceholder}>
            <FormattedMessage id="ResponsiveImage.noImage" />
          </div>
        </div>
      );
    }

    const imageVariants = image.attributes.variants;
    const srcSet = Object.keys(imageVariants)
      .map(variantName => {
        const variant = imageVariants[variantName];
        if (!variant) return null;
        if (variantName === 'scaled-large') {
          return `${variant.url} ${variant.width}w`;
        }
      })
      .filter(v => v != null)
      .join(', ');

    const firstVariantKey = Object.keys(imageVariants)[0];
    const fallbackSrc = imageVariants[firstVariantKey]?.url || '';

    return (
      <div
        key={index}
        className={classNames(css.imageWrapper, className)}
        onClick={() => handleImageClick(index)}
      >
        <img
          className={css.image}
          src={fallbackSrc}
          srcSet={srcSet}
          alt={alt}
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          loading="lazy"
        />
      </div>
    );
  };

  const renderModal = () => {
    if (selectedImageIndex === null) return null;

    const currentImage = images[selectedImageIndex];
    const alt = intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: selectedImageIndex + 1, count: images.length }
    );

    if (!currentImage || !currentImage.attributes || !currentImage.attributes.variants) {
      return null;
    }

    const imageVariants = currentImage.attributes.variants;

    const srcSet = Object.keys(imageVariants)
      .map(variantName => {
        const variant = imageVariants[variantName];
        if (!variant) return null;
        if (variantName === 'scaled-large') {
          return `${variant.url} ${variant.width}w`;
        }
      })
      .filter(v => v != null)
      .join(', ');

    const firstVariantKey = Object.keys(imageVariants)[0];
    const fallbackSrc = imageVariants[firstVariantKey]?.url || '';

    return (
      <div
        className={css.modal}
        onClick={handleCloseModal}
        role="dialog"
        aria-modal="true"
        aria-label="Image gallery modal"
      >
        <div className={css.modalContent} onClick={e => e.stopPropagation()}>
          <button
            className={css.closeButton}
            onClick={handleCloseModal}
            aria-label="Close image modal"
            title="Close (Esc)"
          >
            ×
          </button>
          <div
            className={css.modalImageWrapper}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              className={css.modalImage}
              src={fallbackSrc}
              srcSet={srcSet}
              alt={alt}
              loading="eager"
            />
          </div>
          {images.length > 1 && (
            <div className={css.navigation}>
              <button
                className={css.navButton}
                onClick={handlePreviousImage}
                aria-label="Previous image"
                title="Previous image (←)"
              >
                ‹
              </button>
              <span className={css.imageCounter}>
                {selectedImageIndex + 1} / {images.length}
              </span>
              <button
                className={css.navButton}
                onClick={handleNextImage}
                aria-label="Next image"
                title="Next image (→)"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGrid = () => {
    const imageCount = images.length;

    switch (imageCount) {
      case 1:
        return <div className={css.singleImage}>{renderImage(images[0], 0)}</div>;

      case 2:
        return (
          <div className={css.twoImages}>
            {renderImage(images[0], 0)}
            {renderImage(images[1], 1)}
          </div>
        );

      case 3:
        return (
          <div className={css.threeImages}>
            {renderImage(images[0], 0)}
            {renderImage(images[1], 1)}
            {renderImage(images[2], 2)}
          </div>
        );

      case 4:
        return (
          <div className={css.fourImages}>
            <div className={css.leftSection}>
              <div className={css.topRow}>
                {renderImage(images[0], 0)}
                {renderImage(images[1], 1)}
              </div>
              <div className={css.bottomRow}>{renderImage(images[2], 2)}</div>
            </div>
            <div className={css.rightSection}>{renderImage(images[3], 3)}</div>
          </div>
        );

      case 5:
        return (
          <div className={css.fiveImages}>
            <div className={css.leftSection}>
              <div className={css.topRow}>
                {renderImage(images[0], 0)}
                {renderImage(images[1], 1)}
              </div>
              <div className={css.bottomRow}>{renderImage(images[2], 2)}</div>
            </div>
            <div className={css.rightSection}>
              <div className={css.rightRow}>
                {renderImage(images[3], 3)}
                {renderImage(images[4], 4)}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className={css.sixImages}>
            <div className={css.leftSection}>
              <div className={css.topRow}>
                {renderImage(images[0], 0)}
                {renderImage(images[1], 1)}
              </div>
              <div className={css.bottomRow}>{renderImage(images[2], 2)}</div>
            </div>
            <div className={css.rightSection}>
              <div className={css.topRow}>
                {renderImage(images[3], 3)}
                {renderImage(images[4], 4)}
              </div>
              <div className={css.bottomRow}>{renderImage(images[5], 5)}</div>
            </div>
          </div>
        );

      default:
        // For more than 6 images, show first 6 and indicate there are more
        return (
          <div className={css.sixImages}>
            <div className={css.leftSection}>
              <div className={css.topRow}>
                {renderImage(images[0], 0)}
                {renderImage(images[1], 1)}
              </div>
              <div className={css.bottomRow}>{renderImage(images[2], 2)}</div>
            </div>
            <div className={css.rightSection}>
              <div className={css.topRow}>
                {renderImage(images[3], 3)}
                {renderImage(images[4], 4)}
              </div>
              <div className={css.bottomRow}>
                <div className={css.moreImagesOverlay} onClick={() => handleImageClick(5)}>
                  {renderImage(images[5], 5)}
                  <div className={css.moreImagesText}>+{images.length - 6} more</div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes} data-testid="image-layout-container">
      {renderGrid()}
      {renderModal()}
    </section>
  );
};

ImageLayoutContainer.defaultProps = {
  rootClassName: null,
  className: null,
};

ImageLayoutContainer.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  images: PropTypes.arrayOf(propTypes.image).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(ImageLayoutContainer);
